import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  DoCheck,
  effect,
  ElementRef,
  HostListener,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { Modal } from 'flowbite';
import moment from 'moment';
import { filter, merge, Subject, Subscription } from 'rxjs';
import { debounceTime, skip } from 'rxjs/operators';
import {
  IMapConfig,
  IRouteDetails,
  IRouteDraft,
  IRouteScheduledUpdate,
  IRouteStopRow,
  IRouteStopsPayload,
  IRouteTimes,
  IMapSnapshot,
  IRoutePreview,
  IStopDeletionWarning,
  IStopUsage,
  ICustomToggleOptions,
} from 'src/app/models';
import {
  CreationSegmentsType,
  CustomToggleTone,
  DesignMode,
  ExpeditionType,
  FeatureType,
  ImportExportType,
  KeyCodes,
  LegendType,
  MapTypes,
  NotificationTypes,
  RouteEditMode,
  RoutePathType,
  RouteSaveAction,
  RouteStatus,
  StopType,
} from 'src/app/models/enums';
import {
  EventsOptions,
  LiteralService,
  MapHistoryService,
  RouteTransferService,
  ServiceWizardService,
  TownsService,
  UnsavedChangesService,
  UtilsService,
} from 'src/app/services';
import { UtilsGeo } from 'src/app/services/route-design/UtilsGeo';
import { RouteGraphService } from 'src/app/services/route-design/route-graph.service';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';
import { images } from 'src/images';
import { APP } from 'src/app/config/app';
import { AnimationOptions } from 'ngx-lottie';

@Component({
  selector: 'app-route-design-toolbar',
  templateUrl: './route-design-toolbar.component.html',
  styleUrl: './route-design-toolbar.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class RouteDesignToolbarComponent
  implements OnInit, AfterViewInit, DoCheck, OnDestroy
{
  readonly mode = input<RouteEditMode>(RouteEditMode.DESIGN);
  readonly map = input<google.maps.Map>();
  readonly detailsWidth = input<number>(32);
  readonly route = input<IRouteDetails>();
  readonly newRoute = input<boolean>(false);
  readonly newAlteration = input<boolean>(false);
  readonly orderedStopsOpenRequest = input(0);
  readonly orderedStopsTownStops = input<any[]>([]);
  readonly orderedStopsTownCenter = input<any>(undefined);
  readonly orderedStopsApplying = input<boolean>(false);
  public orderedStopsInitialRows: any[] = [];
  public orderedStopsSelectedStopIds: number[] = [];
  public hasMissingSegments = false;
  readonly orderedStopsRevealMissingSegmentsRequest = signal(0);
  readonly wizardMode = input<boolean>(false);
  readonly isRouteValidForSave = input<boolean>(false);
  readonly isRouteModified = input<boolean>(false);
  readonly isAlterationValidForPreview = input<boolean>(false);
  readonly isAlterationModified = input<boolean>(false);
  readonly alterationSaveDisabled = input<boolean>(true);
  readonly color = input<string>();
  readonly draft = input<IRouteDraft | null>(null);
  readonly scheduled = input<IRouteScheduledUpdate | null>(null);
  readonly previewChanges = input<IRoutePreview[]>([]);
  readonly loadingPreview = input<boolean>(false);
  readonly confirmChangesModalShowed = input<boolean>(false);

  readonly onClose = output();
  readonly orderedStopsConfirmEventEmitter = output<any>();
  readonly maxTimeRaiseEventEmitter = output<number>();
  private shortestPathSeconds: number | null = null;
  private coverageSeconds: number | null = null;
  private shortestPathStopIds: number[] | null = null;
  private coverageStopIds: number[] | null = null;
  private singleStopPath = false;
  private orderedStopsPathTypeOverride: RoutePathType | null = null;
  private previousRoute?: IRouteDetails;
  private previousExpeditionType?: ExpeditionType;
  private routeGraphComputationTimeout: any;
  private routeGraphComputationGeneration = 0;
  private routeTimesComputationGeneration = 0;
  readonly onAlterations = output();
  readonly onRestrictions = output();
  readonly onSave = output<RouteSaveAction>();
  readonly onChangeStatus = output<RouteStatus>();
  readonly onRequestPreview = output();
  readonly onClearPreview = output();
  readonly alterationChangesDetected = output<boolean>();

  public utilsGeo = new UtilsGeo();

  public images = images;
  public DesignMode = DesignMode;
  readonly designModeToggleOptions: ICustomToggleOptions<DesignMode> = [
    {
      value: DesignMode.DESIGN_ROUTE,
      image: images.routes.design,
      descriptionKey: 'a',
      tone: CustomToggleTone.SECONDARY,
    },
    { value: DesignMode.VIEW, image: images.area, descriptionKey: 'a', tone: CustomToggleTone.SECONDARY },
  ];
  public LegendTypes = LegendType;
  public RouteEditMode = RouteEditMode;
  public TooltipPosition = TooltipPosition;
  public FeatureType = FeatureType;
  public APP = APP;

  private originalTerrainSnapshot = '';
  private currentTerrainSnapshot = signal<string>('');
  private originalOutlineSnapshot = '';
  private currentOutlineSnapshot = signal<string>('');
  private alterationHasChanges = computed(
    () =>
      this.isAlterationModified() ||
      this.currentTerrainSnapshot() !== this.originalTerrainSnapshot,
  );
  public saveDisabled = computed(() => {
    if (this.wizardMode()) return true;
    if (this.mode() === RouteEditMode.DESIGN) {
      if (this.newRoute()) return !this.isRouteValidForSave();
      const formChanged = this.isRouteModified() && this.isRouteValidForSave();
      return !this.terrainOrOutlineChanged() && !formChanged;
    }
    if (this.newAlteration()) return this.alterationSaveDisabled();
    return this.alterationSaveDisabled() || !this.alterationHasChanges();
  });
  public alterationPreviewDisabled = computed(() => {
    if (this.newRoute()) return true;
    if (this.mode() !== RouteEditMode.ALTERATIONS) return false;
    if (this.newAlteration()) return !this.isAlterationValidForPreview();
    return !this.isAlterationValidForPreview() || !this.alterationHasChanges();
  });

  public creationSegments = false;
  public designMode = DesignMode.DESIGN_ROUTE;
  public creationSegmentsType = CreationSegmentsType.AUTO;
  public creationSegmentsTypeOptions = Object.values(CreationSegmentsType).map(
    (type: CreationSegmentsType) => {
      return {
        id: type,
        value: type,
        label: `services.routes.design.creationSegmentsType.${type.toLowerCase()}`,
      };
    },
  );
  public creationSegmentsTypeOpened = false;
  public CreationSegmentsType = CreationSegmentsType;

  public importOpened = false;
  public downloadOpened = false;
  public saveOpened = false;
  public RouteSaveAction = RouteSaveAction;
  public saveActions = Object.values(RouteSaveAction).map(
    (action: RouteSaveAction) => {
      return {
        id: action,
        value: action,
        label: `services.routes.design.saveActions.${action.toLowerCase()}`,
      };
    },
  );
  public ImportExportType = ImportExportType;
  private pendingImportType: ImportExportType | null = null;
  readonly importInput = viewChild<ElementRef<HTMLInputElement>>('importInput');
  public importExportOptions = Object.values(ImportExportType).map(
    (type: ImportExportType) => {
      return {
        id: type,
        value: type,
        label: `services.routes.design.importExportTypes.${type.toLowerCase()}`,
      };
    },
  );

  public creationOutline = false;

  public routeStatuses = computed(() =>
    Object.values(RouteStatus).map((status: RouteStatus) => {
      return {
        id: status,
        value: status,
        label: `services.routes.design.routeStatus.${status.toLowerCase()}`,
        disabled: this.newRoute() && status !== RouteStatus.ACTIVE,
      };
    }),
  );
  public routeStatusSelected = RouteStatus.ACTIVE;
  public routeStatusOpened = false;
  public routeStatusInfo = computed(() => {
    const lines: string[] = [];
    if (this.draft())
      lines.push(
        this.literalService.get(
          'services.routes.design.routeStatusInfo.draft',
          true,
        ),
      );
    if (this.scheduled()) {
      lines.push(
        this.literalService.get(
          'services.routes.design.routeStatusInfo.scheduled',
          true,
          {
            date: moment(this.scheduled()!.dateTime).format('DD/MM/YYYY'),
          },
        ),
      );
    }
    return lines.join('\n');
  });

  public selectedType = '';
  public featureList: any = [];
  public selectedFeatures: any;

  private selectedLegendTypeValue: LegendType | undefined = undefined;
  public showLegendDropdown = false;
  public legendTypes = [];

  public canUndo$ = this.mapHistoryService.canUndo$;
  public canRedo$ = this.mapHistoryService.canRedo$;

  private historySubscription: Subscription | null = null;
  private dataLayerAttachedSubscription: Subscription | null = null;
  private mapChangeTrigger$ = new Subject<void>();
  private restoringCount = 0;
  private editSessionId = 0;

  public mapConfigOpened = false;
  public alterationsOpened = false;
  private skipNextDocumentClick = false;
  public MapTypes = MapTypes;

  public stopActions: any[] = [];
  public unsetStopActions: any[] = [];
  public stopTypes: any[] = [];
  public StopType = StopType;

  public routeTimes: IRouteTimes | null = null;
  public loadingRouteTimes: boolean = false;
  public loadingAnimation: AnimationOptions = {
    path: '/assets/animations/loading.json',
  };

  public bulkRadius: number | null = null;
  public bulkRadiusFocused = false;
  public bulkDrivingSpeed: number | null = null;
  public bulkSpeedFocused = false;

  public sharedStopModal: Modal;
  public sharedStopModalShowed = false;
  public deleteAreaModal: Modal;
  public deleteAreaModalShowed = false;
  public deleteAreaText = '';
  public stopInUseModal: Modal;
  public stopInUseModalShowed = false;
  public stopInUseText = '';
  public stopInUseOnlyBlocked = false;
  private pendingStopDeletion: IStopDeletionWarning | null = null;

  constructor(
    public router: Router,
    public literalService: LiteralService,
    public eventsOptions: EventsOptions,
    public utilsService: UtilsService,
    public cdr: ChangeDetectorRef,
    private eRef: ElementRef,
    public mapHistoryService: MapHistoryService,
    private townsService: TownsService,
    private routeTransferService: RouteTransferService,
    private routeGraphService: RouteGraphService,
    private serviceWizardService: ServiceWizardService,
    private unsavedChangesService: UnsavedChangesService,
  ) {
    effect(() => {
      this.eventsOptions.areasEditable = this.mode() === RouteEditMode.DESIGN;
    });
    effect(() => {
      if (this.map()) this.initModalData();
    });
    effect(() => {
      if (this.orderedStopsOpenRequest() > 0)
        this.initModalData(APP.ORDERED_STOPS_TYPE as any);
    });
    effect(() => {
      const changed =
        this.mode() === RouteEditMode.ALTERATIONS &&
        !this.newAlteration() &&
        this.alterationHasChanges();
      this.alterationChangesDetected.emit(changed);
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (this.skipNextDocumentClick) {
      this.skipNextDocumentClick = false;
      return;
    }
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.closeAllDropdowns();
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const alt = event.altKey;
    const shift = event.shiftKey;

    if (alt) event.preventDefault();
    switch (event.code) {
      case KeyCodes.ESCAPE:
        const active = document.activeElement as HTMLElement;
        const tag = active?.tagName?.toLowerCase();
        if (
          tag === 'input' ||
          tag === 'textarea' ||
          tag === 'select' ||
          active?.isContentEditable
        ) {
          active.blur();
          return;
        }
        this.resetInfoBarState();
        this.eventsOptions.cancelOnGoingStopAction();
        this.eventsOptions.hideContextMenus();
        this.eventsOptions.clearSelectedStops();
        this.eventsOptions.clearSelectedSegments();
        this.eventsOptions.clearSelectedAreas();
        if (this.selectedLegendType) this.selectedLegendType = undefined;
        break;
      case KeyCodes.BACKSPACE:
      case KeyCodes.DELETE:
        if (alt) this.delete();
        break;
      case KeyCodes.KEY_D:
        if (alt) this.changeCreationSegments(!this.creationSegments);
        break;
      case KeyCodes.KEY_E:
        if (alt && !this.newRoute()) this.toggleDownload();
        break;
      case KeyCodes.KEY_I:
        if (alt) this.toggleImport();
        break;
      case KeyCodes.KEY_M:
        if (alt) this.config();
        break;
      case KeyCodes.KEY_O:
        if (alt && this.mode() === RouteEditMode.DESIGN)
          this.changeCreationOutline(!this.creationOutline);
        break;
      case KeyCodes.KEY_P:
        if (alt) this.selectLegendType(this.LegendTypes.AFFECTED_RESERVATIONS);
        break;
      case KeyCodes.KEY_S:
        if (alt) this.toggleSave();
        break;
      case KeyCodes.KEY_Y:
        if (alt && this.canRedo$.value) this.redo();
        break;
      case KeyCodes.KEY_Z:
        if (alt) {
          if (shift) {
            if (this.canRedo$.value) this.redo();
          } else if (this.canUndo$.value) this.undo();
        }
        break;
    }
  }

  get mapConfig() {
    return this.eventsOptions.mapConfig;
  }

  get selectedLegendType() {
    return this.selectedLegendTypeValue;
  }
  set selectedLegendType(value: LegendType | undefined) {
    this.selectedLegendTypeValue = value;
    this.eventsOptions.legendType = value;
  }

  ngOnInit(): void {
    this.literalService.setTitle('sidebar.services', ' - NEMI Backoffice');
    this.initStopTypes();

    this.dataLayerAttachedSubscription =
      this.eventsOptions.dataLayerAttached$.subscribe(() => {
        this.initEditSession();
      });
    if (this.eventsOptions.isDataLayerAttached) this.initEditSession();

    this.eventsOptions.currentAreas$.subscribe((areas) => {
      if (areas.length > 0) {
        if (this.selectedType !== FeatureType.OUTLINE || this.hasFeatureSelectionChanged(this.selectedFeatures, areas))
          this.initModalData(FeatureType.OUTLINE);
      } else if (this.selectedType === FeatureType.OUTLINE) {
        this.selectedFeatures = [];
      }
    });

    this.eventsOptions.updatedAreas$.subscribe((areas) => {
      if (areas.length > 1) return;
      this.eventsOptions.clearSelectedAreas();
      if (this.selectedType === FeatureType.OUTLINE) this.initModalData('' as FeatureType);
    });

    this.eventsOptions.currentSegments$.subscribe((segments) => {
      if (segments.length > 0) this.eventsOptions.clearSelectedAreas();
      if (
        segments.length > 0 &&
        this.hasFeatureSelectionChanged(this.selectedFeatures, segments)
      ) {
        this.initModalData(FeatureType.SEGMENT);
        if (segments.length > 1) {
          const drivingSpeeds = new Set<number>(
            segments.map((f: any) =>
              this.utilsGeo.calculateDrivingSpeed(
                f.getProperty('distance'),
                f.getProperty('weight'),
              ),
            ),
          );
          this.bulkDrivingSpeed =
            drivingSpeeds.size === 1 ? [...drivingSpeeds][0] : null;
        } else if (segments.length === 1) {
          this.bulkDrivingSpeed = null;
        }
      } else if (segments.length === 0) {
        this.selectedFeatures = [];
        this.bulkDrivingSpeed = null;
      }
    });

    this.eventsOptions.currentStops$.subscribe((stops) => {
      if (stops.length > 0) this.eventsOptions.clearSelectedAreas();
      this.orderedStopsSelectedStopIds = stops.map((stop: any) =>
        Number(stop.getProperty('id')),
      );
      if (
        stops.length > 0 &&
        this.hasFeatureSelectionChanged(this.selectedFeatures, stops)
      ) {
        if (this.creationSegments) {
          this.selectedFeatures = [...this.eventsOptions.getSelectedStops()];
        } else {
          if (this.selectedLegendType !== LegendType.STOPS) {
            if (stops.length === 1) {
              this.initStopActions(stops[0]);
              this.selectedLegendType = LegendType.STOP_INFO;
              this.bulkRadius = null;
            } else {
              if (
                this.eventsOptions.isShowingSpecificStopType !==
                StopType.ALTERNATIVE_OF
              ) {
                this.selectedLegendType = undefined;
                if (stops.length > 1) {
                  const radiuses = new Set<number>(
                    stops.map((f: any) => f.getProperty('radius')),
                  );
                  this.bulkRadius =
                    radiuses.size === 1 ? [...radiuses][0] : null;
                }
              }
            }
          }
          this.initModalData(
            this.isOrderedStopsTypeSelected ? undefined : FeatureType.STOP,
          );
        }
      } else if (stops.length === 0) {
        if (
          !this.creationSegments &&
          this.selectedLegendType &&
          this.selectedLegendType !== LegendType.STOPS
        )
          this.closeLegend();
        this.selectedFeatures = [];
        this.bulkRadius = null;
      }
    });

    this.eventsOptions.updatedStop$.subscribe((updatedStop) => {
      if (
        updatedStop &&
        this.eventsOptions.getSelectedStops().length === 1 &&
        this.selectedLegendType !== LegendType.STOPS
      ) {
        this.selectedLegendType = LegendType.STOP_INFO;
      }
      this.initModalData(updatedStop ? FeatureType.STOP : undefined);
    });

    this.eventsOptions.stopDragged$.subscribe((stop) => {
      this.checkIfIsASharedStop(stop.id);
    });

    this.eventsOptions.stopDeletionWarning$.subscribe((warning) => {
      this.showStopInUseAlert(warning);
    });

    this.eventsOptions.updatedSegment$
      .pipe(filter((v) => v !== null))
      .subscribe(() => {
        this.initModalData(FeatureType.SEGMENT);
      });

    this.eventsOptions.deletedFeature$.subscribe(() => {
      this.initModalData();
    });

    this.eventsOptions.addedStop$.subscribe(() => {
      this.initModalData();
    });

    this.eventsOptions.mapConfigChange$.subscribe(() => {
      this.initModalData();
    });

    this.eventsOptions.mapClicked$.subscribe(() => {
      if (this.showLegendDropdown) this.showLegendDropdown = false;
      if (this.selectedLegendType === LegendType.AFFECTED_RESERVATIONS)
        this.selectedLegendType = undefined;
    });

    this.eventsOptions.currentCreationSegments$.subscribe(
      (creationSegments) => {
        this.creationSegments = creationSegments;
      },
    );

    this.eventsOptions.currentCreationOutline$.subscribe((creationOutline) => {
      this.creationOutline = creationOutline;
      this.eventsOptions.outlineColor = this.color();
    });

    this.map()?.addListener('click', () => {
      this.closeLegend();
    });

    this.creationSegmentsType = this.eventsOptions.creationSegmentsType;
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  ngDoCheck(): void {
    const route = this.route();
    const expeditionType = route?.expeditionType;
    const sameRoute = route === this.previousRoute;
    if (sameRoute && expeditionType === this.previousExpeditionType) return;
    this.previousRoute = route;
    this.previousExpeditionType = expeditionType;
    if (sameRoute) this.onExpeditionTypeChange(expeditionType);
  }

  private onExpeditionTypeChange(expeditionType?: ExpeditionType) {
    if (!expeditionType || expeditionType === ExpeditionType['']) {
      this.selectedType = '';
      return;
    }
    if (!this.hasOrderedStops) {
      this.initModalData(FeatureType.STOP);
      return;
    }
    this.orderedStopsPathTypeOverride = null;
    this.initModalData(APP.ORDERED_STOPS_TYPE as any);
    this.refreshOrderedStopsRows();
  }

  initStopTypes() {
    this.stopTypes = Object.values(StopType)
      .filter((type: StopType) => type !== StopType.DEMAND)
      .map((type: StopType) => {
        return {
          id: type,
          img: images.routes.stops[
            `${type}Disabled` as keyof typeof images.routes.stops
          ],
          imgSelected:
            images.routes.stops[
              `${type}Selected` as keyof typeof images.routes.stops
            ],
          selected: false,
          visible: type === StopType.ALTERNATIVE_OF ? false : true,
        };
      });
    if (this.selectedType === FeatureType.STOP)
      this.eventsOptions.clearSelectedStops();
    else this.eventsOptions.clearSelectedSegments();
  }

  initStopActions(stop: any) {
    this.stopActions = [];
    this.unsetStopActions = [];
    if (stop.getProperty('start')) {
      this.unsetStopActions.push({
        id: StopType.START,
        label: `services.routes.design.actions.stop.unsetStart`,
        img: images.cancelCircle,
      });
    } else {
      this.stopActions.push({
        id: StopType.START,
        label: `services.routes.design.actions.stop.start`,
        img: images.routes.stops.start,
      });
    }
    if (stop.getProperty('end')) {
      this.unsetStopActions.push({
        id: StopType.END,
        label: `services.routes.design.actions.stop.unsetEnd`,
        img: images.cancelCircle,
      });
    } else {
      this.stopActions.push({
        id: StopType.END,
        label: `services.routes.design.actions.stop.end`,
        img: images.routes.stops.end,
      });
    }
    if (stop.getProperty('inflexion')) {
      this.unsetStopActions.push({
        id: StopType.INFLEXION,
        label: `services.routes.design.actions.stop.unsetInflexion`,
        img: images.cancelCircle,
      });
    } else {
      this.stopActions.push({
        id: StopType.INFLEXION,
        label: `services.routes.design.actions.stop.inflexion`,
        img: images.routes.stops.inflexion,
      });
    }
    if (stop.getProperty('demand')) {
      this.unsetStopActions.push({
        id: StopType.DEMAND,
        label: `services.routes.design.actions.stop.unsetDemand`,
        img: images.cancelCircle,
      });
    } else {
      this.stopActions.push({
        id: StopType.DEMAND,
        label: `services.routes.design.actions.stop.demand`,
        img: images.routes.stops.demand,
      });
    }
    if (
      this.eventsOptions.canEditSharedStopFields(Number(stop.getProperty('id')))
    ) {
      if (stop.getProperty('bidirectional')) {
        this.unsetStopActions.push({
          id: StopType.MERGE,
          label: `services.routes.design.actions.stop.unmerge`,
          img: images.cancelCircle,
        });
      } else {
        this.stopActions.push({
          id: StopType.MERGE,
          label: `services.routes.design.actions.stop.merge`,
          img: images.routes.stops.merge,
        });
      }
    }
    this.stopActions.push({
      id: StopType.ALTERNATIVES,
      label: `services.routes.design.actions.stop.alternatives`,
      img: images.routes.stops.alternatives,
    });
    if (stop.getProperty('alternatives')) {
      this.unsetStopActions.push({
        id: 'removeAllAlternatives',
        label: `services.routes.design.actions.stop.removeAllAlternatives`,
        img: images.cancelCircle,
      });
    }
  }

  updateStopTypesVisibility(stop: any) {
    if (!stop) return;
    this.stopTypes.forEach((type) => {
      if (type.id === StopType.ALTERNATIVE_OF) return;
      type.visible = !!stop[type.id];
      type.selected =
        type.visible &&
        this.eventsOptions.isShowingSpecificStopType === type.id;
    });
    const isAlternativeOf = this.featureList.some((s: any) =>
      s.alternatives?.some((a: any) => a.properties.id === stop.id),
    );
    const alternativesType = this.stopTypes.find(
      (t) => t.id === StopType.ALTERNATIVES,
    );
    const alternativesOfType = this.stopTypes.find(
      (t) => t.id === StopType.ALTERNATIVE_OF,
    );
    if (isAlternativeOf && !stop[StopType.ALTERNATIVES]) {
      alternativesType.visible = true;
      alternativesOfType.selected = true;
    } else {
      if (!stop[StopType.ALTERNATIVES]) alternativesType.visible = false;
      alternativesOfType.selected = false;
    }
    if (this.stopTypes.every((t) => !t.selected)) {
      const firstVisible = this.stopTypes.find((t) => t.visible);
      if (firstVisible) firstVisible.selected = true;
    }
    if (this.stopTypes.every((t) => !t.visible)) {
      this.selectedLegendType = undefined;
    }
  }

  closeModals() {
    if (this.sharedStopModalShowed) this.sharedStopModal.hide();
  }

  async onChangeRouteStatus(status: RouteStatus) {
    this.routeStatusOpened = false;
    if (status === this.routeStatusSelected) return;
    if (!(await this.confirmDiscardChanges())) return;
    this.routeStatusSelected = status;
    this.resetToolbarState();
    this.onChangeStatus.emit(status);
  }

  hasUnsavedChanges(): boolean {
    if (this.wizardMode()) return false;
    if (this.mode() === RouteEditMode.DESIGN) return this.terrainOrOutlineChanged() || this.isRouteModified();
    if (this.mode() === RouteEditMode.ALTERATIONS) return this.alterationHasChanges();
    return false;
  }

  private terrainOrOutlineChanged(): boolean {
    return (
      this.currentTerrainSnapshot() !== this.originalTerrainSnapshot ||
      this.currentOutlineSnapshot() !== this.originalOutlineSnapshot
    );
  }

  private confirmDiscardChanges(): Promise<boolean> {
    return this.unsavedChangesService.confirmDiscard(APP.ROUTE_DESIGN_UNSAVED_CHANGES_KEY);
  }

  async goBack() {
    if (!(await this.confirmDiscardChanges())) return;
    this.resetToolbarState();
    this.mapHistoryService.clearHistory(this.route()?.id ?? -1);
    this.map()?.setMapTypeId(MapTypes.DEFAULT);
    this.eventsOptions.resetMapConfig();
    this.onClose.emit();
  }

  toggleImport() {
    if (!this.importOpened) this.resetInfoBarState('import');
    this.importOpened = !this.importOpened;
  }

  import(type: ImportExportType) {
    this.importOpened = false;
    const input = this.importInput()?.nativeElement;
    if (!input) return;
    this.pendingImportType = type;
    input.accept = this.routeTransferService.fileAccept(type);
    input.click();
  }

  async onImportFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const type = this.pendingImportType;
    input.value = '';
    this.pendingImportType = null;
    const map = this.map();
    if (!file || type === null || !map) return;

    try {
      const result = await this.routeTransferService.parseImport(type, file);
      if (result.replace) {
        await this.eventsOptions.replaceRouteWithImport(result.geojson ?? []);
      }
      if (result.plainStops?.length)
        await this.eventsOptions.applyImportedStops(result.plainStops);
      this.eventsOptions.removeDuplicatedTownStops();
      this.eventsOptions.updateMarkers();
      this.initModalData(
        result.plainStops?.length ? FeatureType.STOP : undefined,
      );
      this.pushCurrentSnapshot(this.route()?.id ?? -1);
      this.utilsService.showNotification(
        images.notifications.check,
        'services.routes.design.actions.import.success',
        NotificationTypes.SUCCESS,
      );
    } catch {
      this.utilsService.showNotification(
        images.notifications.warning,
        'services.routes.design.actions.import.error',
        NotificationTypes.DANGER,
      );
    }
  }

  toggleDownload() {
    if (!this.downloadOpened) this.resetInfoBarState('download');
    this.downloadOpened = !this.downloadOpened;
  }

  async download(type: ImportExportType): Promise<void> {
    this.downloadOpened = false;
    const map = this.map();
    if (!map) return;
    try {
      await this.routeTransferService.exportRoute(type, map, this.route());
    } catch {
      this.utilsService.showNotification(
        images.notifications.warning,
        'services.routes.design.actions.export.error',
        NotificationTypes.DANGER,
      );
    }
  }

  closeAllDropdowns(except?: string) {
    if (except !== 'routeStatus') this.routeStatusOpened = false;
    if (except !== 'mapConfig') this.mapConfigOpened = false;
    if (except !== 'creationSegmentsType')
      this.creationSegmentsTypeOpened = false;
    if (except !== 'alterations') this.alterationsOpened = false;
    if (except !== 'import') this.importOpened = false;
    if (except !== 'download') this.downloadOpened = false;
    if (except !== 'save') this.saveOpened = false;
  }

  resetInfoBarState(except?: string) {
    this.closeAllDropdowns(except);
    if (this.creationOutline) this.changeCreationOutline(false);
    if (this.creationSegments && except !== 'creationSegmentsType')
      this.changeCreationSegments(false);
  }

  toggleRouteStatus() {
    if (this.newRoute()) return;
    if (!this.routeStatusOpened) this.resetInfoBarState('routeStatus');
    this.routeStatusOpened = !this.routeStatusOpened;
  }

  config() {
    if (!this.mapConfigOpened) this.resetInfoBarState('mapConfig');
    this.mapConfigOpened = !this.mapConfigOpened;
  }

  closeMapConfig() {
    this.mapConfigOpened = false;
  }

  toggleCreationSegmentsType() {
    this.resetInfoBarState('creationSegmentsType');
    this.creationSegmentsTypeOpened = !this.creationSegmentsTypeOpened;
  }

  async updateMapConfig(valueToChange: keyof IMapConfig, value: any) {
    const currentType = typeof this.mapConfig[valueToChange];
    let parsed: any =
      currentType === 'boolean' || currentType === 'string'
        ? value
        : Math.max(0, +value);
    if (valueToChange === 'radius')
      parsed = this.utilsService.clampStopRadius(+value);
    this.eventsOptions.updateMapConfig(valueToChange, parsed);
  }

  toggleAlterations() {
    if (!this.alterationsOpened) this.resetInfoBarState('alterations');
    this.alterationsOpened = !this.alterationsOpened;
  }

  async selectAlterations() {
    this.alterationsOpened = false;
    if (!(await this.confirmDiscardChanges())) return;
    this.mapHistoryService.clearHistory(this.route()?.id ?? -1);
    this.onAlterations.emit();
  }

  async selectRestrictions() {
    this.alterationsOpened = false;
    if (!(await this.confirmDiscardChanges())) return;
    this.onRestrictions.emit();
  }

  changeCreationSegments(creationSegments: boolean) {
    if (creationSegments) this.resetInfoBarState();
    else this.eventsOptions.restoreCursor();
    this.eventsOptions.changeCreationSegments(
      creationSegments,
      this.creationSegmentsType,
    );
  }

  changeCreationSegmentsType(type: CreationSegmentsType) {
    this.skipNextDocumentClick = true;
    this.creationSegmentsType = type;
    this.eventsOptions.changeCreationSegments(this.creationSegments, type);
    this.creationSegmentsTypeOpened = false;
  }

  changeCreationOutline(creationOutline: boolean) {
    if (creationOutline) this.resetInfoBarState();
    else this.eventsOptions.restoreCursor();
    this.eventsOptions.changeCreationOutline(creationOutline, this.color());
  }

  undo() {
    const routeId = this.route()?.id ?? -1;
    this.eventsOptions.clearSelectedStops();
    this.eventsOptions.clearSelectedSegments();
    this.closeAllDropdowns();
    this.mapHistoryService.undo(routeId).then((snapshot) => {
      if (snapshot) this.restoreSnapshot(snapshot);
    });
  }

  redo() {
    const routeId = this.route()?.id ?? -1;
    this.eventsOptions.clearSelectedStops();
    this.eventsOptions.clearSelectedSegments();
    this.closeAllDropdowns();
    this.mapHistoryService.redo(routeId).then((snapshot) => {
      if (snapshot) this.restoreSnapshot(snapshot);
    });
  }

  delete() {
    if (this.selectedType === FeatureType.OUTLINE) {
      if (this.eventsOptions.targetSelectedArea()) this.showDeleteAreaModal();
      return;
    }
    this.eventsOptions.delete();
  }

  renameAreaFromMenu() {
    const areaId = this.eventsOptions.targetMenuArea();
    if (areaId !== null) this.eventsOptions.setSelectedArea({ ids: [areaId] });
  }

  private hasFeatureSelectionChanged(previous: any[], current: any[]): boolean {
    const previousIds = (previous ?? []).map((feature: any) =>
      feature.getProperty('id'),
    );
    const currentIds = current.map((feature: any) => feature.getProperty('id'));
    return this.utilsService.compareArrays(previousIds, currentIds).length > 0;
  }

  initModalData(type?: FeatureType) {
    if (type !== undefined) {
      this.selectedType = type;
      if ((type as any) !== APP.ORDERED_STOPS_TYPE) {
        this.eventsOptions.showOnlySelectedType(type);
      }
    }
    // The ordered-stops panel always mirrors the current map graph, so it is rebuilt on
    // every map change (stop edits, segment creation/deletion), not only when its tab opens
    if (this.hasOrderedStops) this.scheduleRouteGraphComputation();
    switch (this.selectedType) {
      case FeatureType.STOP:
      case APP.ORDERED_STOPS_TYPE as FeatureType:
        this.featureList = [...this.eventsOptions.getStops()];
        this.selectedFeatures = [...this.eventsOptions.getSelectedStops()];
        if (this.selectedFeatures.length === 1) {
          const stop = this.featureList.find(
            (s: any) => s.id === this.selectedFeatures[0].getProperty('id'),
          );
          if (this.selectedLegendType === LegendType.STOP_INFO)
            this.updateStopTypesVisibility(stop);
        }
        break;
      case FeatureType.OUTLINE:
        this.featureList = this.eventsOptions.getAreas();
        this.selectedFeatures = [...this.eventsOptions.getSelectedAreas()];
        break;
      case FeatureType.SEGMENT:
        this.featureList = [...this.eventsOptions.getSegments()];
        this.selectedFeatures = [...this.eventsOptions.getSelectedSegments()];
        if (
          this.selectedLegendType === this.LegendTypes.STOPS ||
          this.selectedLegendType === this.LegendTypes.STOP_INFO
        )
          this.selectedLegendType = undefined;
        break;
      default:
        this.featureList = [];
        this.selectedFeatures = [];
    }
    if (this.selectedLegendType === LegendType.TIMES) this.getRouteTimes();
  }

  onOrderedStopsConfirm(payload: IRouteStopsPayload) {
    this.orderedStopsConfirmEventEmitter.emit({
      ...payload,
      creationType: this.creationSegmentsType,
    });
  }

  onOrderedStopsSelect(stopId: number | null) {
    if (stopId == null) {
      if (this.eventsOptions.getSelectedStops().length > 0)
        this.eventsOptions.clearSelectedStops();
      return;
    }
    this.eventsOptions.setSelectedStop(
      { ids: [stopId], multiple: false },
      true,
    );
  }

  onOrderedStopsPathTypeChange(pathType: RoutePathType) {
    this.orderedStopsPathTypeOverride = pathType;
    this.refreshOrderedStopsRows();
  }

  get orderedStopsPathType(): RoutePathType {
    if (this.orderedStopsPathTypeOverride)
      return this.orderedStopsPathTypeOverride;
    return this.route()?.expeditionType === ExpeditionType.Fixed
      ? RoutePathType.SHORTEST
      : RoutePathType.LONGEST;
  }

  get isOrderedStopsTypeSelected(): boolean {
    return (this.selectedType as any) === APP.ORDERED_STOPS_TYPE;
  }

  // A hybrid route can be reordered too while it has no shortcut nor exclusive path: one stop after another
  get allowReorderStops(): boolean {
    const expeditionType = this.route()?.expeditionType;
    return (
      expeditionType === ExpeditionType.Fixed ||
      (expeditionType === ExpeditionType.HybridWithFixedPoints &&
        this.singleStopPath)
    );
  }

  private raiseMaxTimeIfPathExceedsIt() {
    // Fixed routes must keep maxTime above the coverage route time; it is only ever raised,
    // never lowered, since a higher value may have been set on purpose
    const route: any = this.route();
    if (!route || this.coverageSeconds == null || route.expeditionType !== ExpeditionType.Fixed) return;
    const requiredMinutes = Math.floor(this.coverageSeconds / 60) + 1;
    if ((route.maxTime ?? 0) < requiredMinutes)
      this.maxTimeRaiseEventEmitter.emit(requiredMinutes);
  }

  get coverageExceedsMaxTime(): boolean {
    const maxTimeMinutes = this.route()?.maxTime;
    const coverageSeconds = this.routeTimes?.maxPathTime;
    return maxTimeMinutes != null && coverageSeconds != null && coverageSeconds > maxTimeMinutes * 60;
  }

  private get hasOrderedStops(): boolean {
    const expeditionType = this.route()?.expeditionType;
    return (
      !!expeditionType &&
      expeditionType !== ExpeditionType[''] &&
      expeditionType !== ExpeditionType.DynamicDoor2Door &&
      expeditionType !== ExpeditionType.HybridDoor2Door
    );
  }

  get showAreasTab(): boolean {
    return this.mode() === RouteEditMode.DESIGN && (this.route()?.areas?.length ?? 0) > 1;
  }

  get showOrderedStopsTab(): boolean {
    return this.selectedType !== '' && this.hasOrderedStops;
  }

  private scheduleRouteGraphComputation() {
    // Path exploration on a big graph can be expensive, so it is debounced and delegated
    // to a web worker; a newer schedule supersedes any in-flight result
    clearTimeout(this.routeGraphComputationTimeout);
    this.routeGraphComputationTimeout = setTimeout(
      () => this.computeRouteGraph(),
      250,
    );
  }

  private async computeRouteGraph() {
    const generation = ++this.routeGraphComputationGeneration;
    const graphRequest = this.eventsOptions.buildRouteGraphRequest();
    if (graphRequest) {
      const result = await this.routeGraphService.computePaths(graphRequest);
      if (generation !== this.routeGraphComputationGeneration) return;
      this.shortestPathSeconds = result.shortestPathSeconds;
      this.coverageSeconds = result.coverageSeconds;
      this.shortestPathStopIds = result.shortestPathStopIds;
      this.coverageStopIds = firstVisits(result.coverageStopIds);
      this.singleStopPath = sameStopPath(
        result.shortestPathStopIds,
        result.coverageStopIds,
      );
    } else {
      this.shortestPathSeconds = null;
      this.coverageSeconds = null;
      this.shortestPathStopIds = null;
      this.coverageStopIds = null;
      this.singleStopPath = false;
    }
    this.refreshOrderedStopsRows();
    this.raiseMaxTimeIfPathExceedsIt();
    this.cdr.detectChanges();
  }

  private refreshOrderedStopsRows() {
    this.orderedStopsInitialRows = this.buildOrderedStopsRows();
    this.hasMissingSegments = this.orderedStopsInitialRows.some(
      (row: IRouteStopRow) => row.breakRow,
    );
  }

  private buildOrderedStopsRows(): any[] {
    const stops = this.eventsOptions
      .getStops()
      .filter((stop: any) => stop.type === FeatureType.STOP);
    if (stops.length === 0) return [];
    const segments = this.eventsOptions.getSegments();
    const stopsById = new Map<number, any>(
      stops.map((stop: any) => [stop.id, stop]),
    );
    const outgoingSegmentsByStopId = new Map<number, any[]>();
    segments.forEach((segment: any) => {
      const segmentStartId = segment.start?.properties?.id;
      if (segmentStartId == null) return;
      if (!outgoingSegmentsByStopId.has(segmentStartId))
        outgoingSegmentsByStopId.set(segmentStartId, []);
      outgoingSegmentsByStopId.get(segmentStartId)!.push(segment);
    });
    const startStop: any = stops.find((stop: any) => stop.start);
    const endStop: any = stops.find((stop: any) => stop.end);
    const rowOf = (stop: any) => ({
      stopId: stop.id,
      name: stop.name,
      latitude: stop.lat,
      longitude: stop.lng,
    });
    if (!startStop && !endStop) return [];
    const isCyclic = !!startStop && !!endStop && startStop.id === endStop.id;

    const pathStopIds =
      this.orderedStopsPathType === RoutePathType.SHORTEST
        ? this.shortestPathStopIds
        : this.coverageStopIds;
    if (endStop && pathStopIds && pathStopIds.length >= 2) {
      const pathStops = pathStopIds
        .map((stopId) => stopsById.get(stopId))
        .filter(Boolean);
      if (pathStops.length === pathStopIds.length) return pathStops.map(rowOf);
    }

    // No complete path between start and end: show each connected chain with a break in between
    const visited = new Set<number>();
    const walkChain = (firstStop: any): any[] => {
      const chain: any[] = [];
      let current: any = firstStop;
      while (current && !visited.has(current.id)) {
        chain.push(current);
        visited.add(current.id);
        const nextSegment = (outgoingSegmentsByStopId.get(current.id) ?? [])
          .filter(
            (segment: any) =>
              !visited.has(segment.end?.properties?.id) &&
              stopsById.has(segment.end?.properties?.id),
          )
          .sort(
            (firstSegment: any, secondSegment: any) =>
              (firstSegment.weight ?? 0) - (secondSegment.weight ?? 0),
          )[0];
        current = nextSegment
          ? stopsById.get(nextSegment.end?.properties?.id)
          : undefined;
      }
      return chain;
    };
    const routeStopIds = new Set<number>(
      segments.flatMap((segment: any) => [
        segment.start?.properties?.id,
        segment.end?.properties?.id,
      ]),
    );
    if (startStop) routeStopIds.add(startStop.id);
    if (endStop) routeStopIds.add(endStop.id);
    const routeStops = stops.filter((stop: any) => routeStopIds.has(stop.id));
    const chains: any[][] = [];
    if (startStop) chains.push(walkChain(startStop));
    while (visited.size < routeStops.length) {
      const remaining = routeStops.filter((stop: any) => !visited.has(stop.id));
      const chainHead =
        remaining.find((stop: any) =>
          segments.every(
            (segment: any) =>
              segment.end?.properties?.id !== stop.id ||
              visited.has(segment.start?.properties?.id) ||
              !stopsById.has(segment.start?.properties?.id),
          ),
        ) ?? remaining[0];
      chains.push(walkChain(chainHead));
    }
    const previousRowIndexByStopId = new Map<number, number>();
    (this.orderedStopsInitialRows ?? []).forEach(
      (row: any, rowIndex: number) => {
        if (row.stopId != null && !previousRowIndexByStopId.has(row.stopId))
          previousRowIndexByStopId.set(row.stopId, rowIndex);
      },
    );
    const previousRankOf = (chain: any[]) =>
      Math.min(
        ...chain.map(
          (stop) => previousRowIndexByStopId.get(stop.id) ?? Infinity,
        ),
      );
    const firstReorderableChain = startStop ? 1 : 0;
    const reorderedChains = chains
      .slice(firstReorderableChain)
      .sort(
        (firstChain, secondChain) =>
          previousRankOf(firstChain) - previousRankOf(secondChain) || 0,
      );
    chains.splice(
      firstReorderableChain,
      reorderedChains.length,
      ...reorderedChains,
    );
    const closesLoop = (stopId: number | undefined) =>
      (outgoingSegmentsByStopId.get(stopId!) ?? []).some(
        (segment: any) => segment.end?.properties?.id === startStop.id,
      );
    // The chain that reaches the end stop is shown last
    const endChainIndex = isCyclic
      ? chains.findIndex(
          (chain, chainIndex) =>
            chainIndex > 0 && closesLoop(chain[chain.length - 1]?.id),
        )
      : endStop
        ? chains.findIndex((chain) =>
            chain.some((stop) => stop.id === endStop.id),
          )
        : -1;
    if (endChainIndex >= 0 && endChainIndex !== chains.length - 1) {
      chains.push(chains.splice(endChainIndex, 1)[0]);
    }
    const rows: any[] = [];
    chains
      .filter((chain) => chain.length > 0)
      .forEach((chain, chainIndex) => {
        if (chainIndex > 0) rows.push({ breakRow: true });
        rows.push(...chain.map(rowOf));
      });
    if (isCyclic) {
      if (!closesLoop(rows[rows.length - 1]?.stopId))
        rows.push({ breakRow: true });
      rows.push(rowOf(startStop));
    }
    if (!endStop) rows.push({ name: '' });
    return rows;
  }

  onClickSelectedItem(event: any) {
    if (this.selectedType === FeatureType.STOP) {
      if (event.ids.length === 0) {
        this.clearSelection();
      } else if (event.ids.length === 1) {
        const stop = this.featureList.find((s: any) => s.id === event.ids[0]);
        if (this.selectedLegendType === LegendType.STOP_INFO)
          this.updateStopTypesVisibility(stop);
      }
      this.eventsOptions.setSelectedStop(event, true);
    } else if (this.selectedType === FeatureType.SEGMENT) {
      this.eventsOptions.setSelectedSegment(event);
    } else if (this.selectedType === FeatureType.OUTLINE) {
      this.eventsOptions.setSelectedArea(event);
    }
  }

  onEditSelectedItem(event: any) {
    if (this.selectedType === FeatureType.OUTLINE) {
      this.eventsOptions.renameArea(event.id, event.value);
      this.initModalData();
      return;
    }
    let type = FeatureType.STOP;
    if (this.selectedType === FeatureType.SEGMENT) type = FeatureType.SEGMENT;
    else if (this.selectedType === FeatureType.STOP) {
      const stop = this.featureList.find((s: any) => s.id === event.id);
      this.checkIfIsASharedStop(stop.id);
    }
    if (event.valueToChange === 'name' && event.value === '') {
      event.value =
        this.route()?.stops.find((stop) => stop.id === event.id)?.name ??
        this.eventsOptions.getStopName(event.id);
    }
    this.eventsOptions.edit(event.id, type, event.valueToChange, event.value);
  }

  selectLegendType(type: LegendType | undefined) {
    this.selectedLegendType = type;
    switch (type) {
      case LegendType.STOPS:
        this.initStopTypes();
        break;
      case LegendType.AFFECTED_RESERVATIONS:
        this.onRequestPreview.emit();
        break;
      case LegendType.TIMES:
        this.getRouteTimes();
        break;
    }
    if (this.showLegendDropdown) this.showLegendDropdown = false;
  }

  clearSelection() {
    this.stopTypes.forEach((type) => (type.selected = false));
    this.selectedLegendType = undefined;
  }

  closeLegend() {
    this.selectedLegendType = undefined;
    this.eventsOptions.clearSelectedStopType();
    this.showLegendDropdown = false;
    this.onClearPreview.emit();
  }

  getFormattedTime(seconds?: number) {
    return this.utilsService.formatSecondsAsTime(seconds);
  }

  toggleSave() {
    if (this.serviceWizardService.active) return;
    // TODO: uncomment when route status is done
    // if (this.saveDisabled()) return;
    // if (this.mode() !== RouteEditMode.DESIGN) {
    this.triggerSave(RouteSaveAction.UPDATE);
    //   return;
    // }
    // if (!this.saveOpened) this.resetInfoBarState('save');
    // this.saveOpened = !this.saveOpened;
  }

  triggerSave(action: RouteSaveAction) {
    this.saveOpened = false;
    if (this.selectedLegendType) this.selectedLegendType = undefined;
    if (this.saveBlockedByMissingSegments) {
      this.revealMissingSegments();
      return;
    }
    this.onSave.emit(action);
  }

  get saveBlockedByMissingSegments(): boolean {
    return (
      this.mode() === RouteEditMode.DESIGN &&
      this.hasOrderedStops &&
      this.hasMissingSegments
    );
  }

  private revealMissingSegments() {
    if (this.selectedType !== APP.ORDERED_STOPS_TYPE)
      this.initModalData(APP.ORDERED_STOPS_TYPE as any);
    this.orderedStopsRevealMissingSegmentsRequest.update(
      (request) => request + 1,
    );
  }

  selectStopType(type: StopType | null) {
    if (this.selectedLegendType === LegendType.STOPS || !type)
      this.eventsOptions.clearSelectedStopType();
    const selectedIndex = this.stopTypes.findIndex(
      (stop) => stop.id !== type && stop.selected,
    );
    if (selectedIndex !== -1) {
      this.stopTypes[selectedIndex].selected = false;
    }
    const stopType = this.stopTypes.find((stop) => stop.id === type);
    if (type && stopType) {
      stopType.selected = true;
      if (stopType.selected) {
        if (this.selectedLegendType === LegendType.STOPS) {
          this.eventsOptions.selectAllStopsOfType(type);
        } else {
          this.eventsOptions.showRelatedStopsOfType(type);
        }
      }
    }
  }

  isAlternativeSelected() {
    const alternativesSelected = this.stopTypes.find(
      (stop) => stop.id === StopType.ALTERNATIVES,
    )?.selected;
    if (
      alternativesSelected &&
      ((this.selectedLegendType === LegendType.STOP_INFO &&
        this.selectedFeatures.length === 1) ||
        this.selectedLegendType === LegendType.STOPS)
    )
      return true;
    return this.stopTypes.find((stop) => stop.id === StopType.ALTERNATIVE_OF)
      ?.selected;
  }

  isStopTypeChecked(type: StopType) {
    if (type === StopType.ALTERNATIVES) return this.isAlternativeSelected();
    return this.stopTypes.find((stop) => stop.id === type)?.selected;
  }

  isStopTypeVisible(type: StopType): boolean {
    const selectedStop = this.selectedFeatures[0];
    if (type === StopType.ALTERNATIVES) {
      const alternatives = selectedStop.getProperty('alternatives');
      return alternatives?.length > 0;
    } else if (type === StopType.ALTERNATIVE_OF) {
      const isAlternativeOf = this.featureList.some((s: any) =>
        s.alternatives?.some(
          (a: any) => a.properties.id === selectedStop.getProperty('id'),
        ),
      );
      return isAlternativeOf;
    }
    return !!this.stopTypes.find((t) => t.id === type)?.visible;
  }

  get selectedStopSubType(): StopType | null {
    if (this.stopTypes.find((t) => t.id === StopType.ALTERNATIVE_OF)?.selected)
      return StopType.ALTERNATIVE_OF;
    if (this.stopTypes.find((t) => t.id === StopType.ALTERNATIVES)?.selected)
      return StopType.ALTERNATIVES;
    return null;
  }

  onStopAction(action: string) {
    const selectedStop = this.selectedFeatures[0];
    switch (action) {
      case StopType.MERGE:
        if (selectedStop.getProperty('bidirectional'))
          this.eventsOptions.unsetMergeStop();
        else this.eventsOptions.setMergeStop();
        break;
      case StopType.START:
        this.eventsOptions.setStartStop();
        break;
      case StopType.END:
        this.eventsOptions.setEndStop();
        break;
      case StopType.INFLEXION:
        this.eventsOptions.setInflexionStop();
        break;
      case StopType.DEMAND:
        this.eventsOptions.setDemandStop();
        break;
      case StopType.ALTERNATIVES:
        this.eventsOptions.setAlternativeStop();
        break;
      case 'removeAllAlternatives':
        this.eventsOptions.removeAllAlternativeStops();
        break;
    }
  }

  async getRouteTimes(): Promise<void> {
    const generation = ++this.routeTimesComputationGeneration;
    const graphRequest = this.eventsOptions.buildRouteGraphRequest();
    if (!graphRequest) {
      this.routeTimes = null;
      this.loadingRouteTimes = false;
      return;
    }
    this.loadingRouteTimes = true;
    const times = await this.routeGraphService.computeRouteTimes(graphRequest);
    if (generation !== this.routeTimesComputationGeneration) return;
    this.routeTimes = times;
    this.loadingRouteTimes = false;
    this.cdr.detectChanges();
  }

  applyBulkUpdate() {
    if (this.bulkRadius !== null)
      this.bulkRadius = this.utilsService.clampStopRadius(this.bulkRadius);
    this.selectedFeatures.map((feature: any) => {
      const id = feature.getProperty('id');
      if (this.selectedType === FeatureType.STOP) {
        this.eventsOptions.edit(
          id,
          FeatureType.STOP,
          'radius',
          this.bulkRadius,
        );
      } else {
        const distanceMeters = feature.getProperty('distance') * 1000;
        const newSeconds = this.utilsGeo.calculateSegmentDuration(
          distanceMeters,
          this.bulkDrivingSpeed!,
        );
        const newTime =
          this.utilsService.convertSecondsToMinutesAndSeconds(newSeconds);
        this.eventsOptions.edit(id, FeatureType.SEGMENT, 'time', newTime);
      }
    });
    this.utilsService.showNotification(
      images.notifications.check,
      `services.routes.design.actions.update.${this.selectedType}`,
      NotificationTypes.SUCCESS,
    );
  }

  checkIfIsASharedStop(id: number) {
    if (this.mode() === RouteEditMode.ALTERATIONS) return;
    const hideSharedStopAlert = JSON.parse(
      localStorage.getItem(APP.LOCAL_STORAGE_HIDE_SHARED_STOP_ALERT)!,
    );
    if (hideSharedStopAlert) return;
    this.eventsOptions.getRoutesUsingStop(id).then((routes) => {
      if (routes.length === 0) return;
      this.sharedStopModal = new Modal(
        document.getElementById('sharedStopAlert'),
        {
          placement: 'center',
          closable: false,
          onHide: () => (this.sharedStopModalShowed = false),
          onShow: () => (this.sharedStopModalShowed = true),
        },
      );
      this.sharedStopModal.toggle();
    });
  }

  showDeleteAreaModal() {
    this.eventsOptions.hideContextMenus();
    this.deleteAreaText = this.literalService.get('services.routes.design.area.deleteConfirm', true, {
      name: this.eventsOptions.getTargetAreaName(),
    });
    this.deleteAreaModal = new Modal(document.getElementById('deleteAreaAlert'), {
      placement: 'center',
      closable: false,
      onHide: () => (this.deleteAreaModalShowed = false),
      onShow: () => (this.deleteAreaModalShowed = true),
    });
    this.deleteAreaModal.show();
  }

  confirmDeleteArea() {
    this.eventsOptions.deleteTargetArea();
    this.deleteAreaModal.hide();
  }

  hideSharedStopAlert(undo?: boolean) {
    if (undo) this.undo();
    this.sharedStopModal.hide();
  }

  confirmSharedStop(notShowAgain: boolean) {
    localStorage.setItem(
      APP.LOCAL_STORAGE_HIDE_SHARED_STOP_ALERT,
      notShowAgain.toString(),
    );
    this.hideSharedStopAlert();
  }

  showStopInUseAlert(warning: IStopDeletionWarning) {
    this.pendingStopDeletion = warning;
    this.stopInUseOnlyBlocked = warning.disconnectStops.length === 0;
    this.stopInUseText = [
      this.describeStopUsages(warning.disconnectStops, 'disconnect'),
      this.describeStopUsages(warning.blockedStops, 'blocked'),
    ]
      .filter(Boolean)
      .join('<br/><br/>');
    this.stopInUseModal = new Modal(document.getElementById('stopInUseAlert'), {
      placement: 'center',
      closable: false,
      onHide: () => (this.stopInUseModalShowed = false),
      onShow: () => (this.stopInUseModalShowed = true),
    });
    this.stopInUseModal.show();
  }

  private describeStopUsages(
    usages: IStopUsage[],
    usageKey: 'disconnect' | 'blocked',
  ): string {
    if (!usages.length) return '';
    const stops = usages
      .map(
        (usage) =>
          `<b>${usage.stop.name}</b> (${usage.routes.map((route) => route.name).join(', ')})`,
      )
      .join(', ');
    return this.literalService.get(
      `services.routes.design.actions.update.stopInUse.${usageKey}`,
      true,
      { stops },
    );
  }

  confirmStopInUse() {
    const pendingStopDeletion = this.pendingStopDeletion;
    this.pendingStopDeletion = null;
    this.stopInUseModal.hide();
    pendingStopDeletion?.proceed();
  }

  cancelStopInUse() {
    this.pendingStopDeletion = null;
    this.stopInUseModal.hide();
  }

  private getTerrainSnapshot(geoJson: any): string {
    if (!geoJson) return '';
    return JSON.stringify(
      this.utilsService.cleanTerrainObject(JSON.parse(JSON.stringify(geoJson))),
    );
  }

  private getOutlineSnapshot(geoJson: any): string {
    return JSON.stringify(this.utilsService.featuresToAreas(geoJson?.features ?? []));
  }

  resetToolbarState() {
    this.resetInfoBarState();
    this.eventsOptions.cancelOnGoingStopAction();
    this.eventsOptions.hideContextMenus();
    this.eventsOptions.clearSelectedStops();
    this.eventsOptions.clearSelectedSegments();
    if (this.selectedLegendType) this.selectedLegendType = undefined;
  }

  ngOnDestroy(): void {
    this.closeModals();
    this.historySubscription?.unsubscribe();
    this.dataLayerAttachedSubscription?.unsubscribe();
    this.mapChangeTrigger$.complete();
  }

  // ─── Map history helpers ───────────────────────────────────────────────────

  private initEditSession(): void {
    this.editSessionId++;
    this.originalTerrainSnapshot = '';
    this.originalOutlineSnapshot = '';
    this.resetToolbarState();
    this.initMapHistory();
  }

  private initMapHistory(): void {
    const routeId = this.route()?.id ?? -1;
    const sessionId = this.editSessionId;

    this.mapHistoryService.initHistory(routeId).then(() => {
      if (sessionId !== this.editSessionId) return;
      this.pushCurrentSnapshot(routeId);
    });

    this.historySubscription?.unsubscribe();
    this.historySubscription = new Subscription();

    this.historySubscription.add(
      merge(
        this.eventsOptions.updatedSegment$.pipe(
          skip(1),
          filter((v) => v !== null),
        ),
        this.eventsOptions.updatedStop$.pipe(
          skip(1),
          filter((v) => v !== null),
        ),
        this.eventsOptions.updatedAreas$,
        this.eventsOptions.deletedFeature$,
        this.eventsOptions.addedStop$,
        this.eventsOptions.currentCreationSegments$.pipe(
          skip(1),
          filter((v) => !v),
        ),
        this.eventsOptions.currentCreationOutline$.pipe(
          skip(1),
          filter((v) => !v),
        ),
      )
        .pipe(debounceTime(800))
        .subscribe(() => {
          if (this.restoringCount === 0) this.pushCurrentSnapshot(routeId);
        }),
    );

    this.historySubscription.add(
      this.eventsOptions.flightPathChange$.subscribe(() => {
        if (this.restoringCount === 0) this.pushCurrentSnapshot(routeId);
      }),
    );
  }

  private pushCurrentSnapshot(routeId: number): void {
    if (this.restoringCount > 0) return;
    const mapInstance = this.map();
    if (!mapInstance) return;
    const sessionId = this.editSessionId;
    const flightPath = this.eventsOptions.getFlightPathSnapshot() ?? undefined;
    const editingWaypointsSegmentId =
      this.eventsOptions.editingWaypointsSegmentId;
    mapInstance.data.toGeoJson((json: any) => {
      if (sessionId !== this.editSessionId || this.restoringCount > 0) return;
      this.mapHistoryService.pushSnapshot(
        routeId,
        json,
        flightPath,
        editingWaypointsSegmentId,
      );
      this.currentTerrainSnapshot.set(this.getTerrainSnapshot(json));
      this.currentOutlineSnapshot.set(this.getOutlineSnapshot(json));
      if (this.originalTerrainSnapshot === '')
        this.originalTerrainSnapshot = this.currentTerrainSnapshot();
      if (this.originalOutlineSnapshot === '')
        this.originalOutlineSnapshot = this.currentOutlineSnapshot();
    });
  }

  private restoreSnapshot(snapshot: IMapSnapshot): void {
    const mapInstance = this.map();
    if (!mapInstance) return;
    this.restoringCount++;
    const editingSegmentId = snapshot.editingWaypointsSegmentId ?? null;
    this.eventsOptions.clearSelectedSegments();
    const toRemove: any[] = [];
    mapInstance.data.forEach((f: any) => toRemove.push(f));
    toRemove.forEach((f) => mapInstance.data.remove(f));
    mapInstance.data.addGeoJson(snapshot.features);
    this.currentTerrainSnapshot.set(this.getTerrainSnapshot(snapshot.features));
    this.currentOutlineSnapshot.set(this.getOutlineSnapshot(snapshot.features));
    const snapshotFeatures: any[] = (snapshot.features as any).features ?? [];
    this.eventsOptions.onUpdateAreas.next(this.utilsService.featuresToAreas(snapshotFeatures));
    this.eventsOptions
      .loadSegmentAndStops(true, mapInstance)
      .then(() => {
        this.eventsOptions.applyMapConfig(mapInstance);
        this.eventsOptions.updateMarkers();
        this.eventsOptions.restoreTownStopsFromMapData();
        if (snapshot.flightPath)
          this.eventsOptions.restoreFlightPath(snapshot.flightPath);
        else {
          if (this.eventsOptions.creationSegments)
            this.eventsOptions.changeCreationSegments(false);
          if (this.eventsOptions.creationOutline)
            this.eventsOptions.changeCreationOutline(false);
        }
        if (editingSegmentId !== null) {
          this.eventsOptions.setSelectedSegment({
            ids: [editingSegmentId],
            multiple: false,
          });
          this.eventsOptions.editWaypointsSegment();
        }
      })
      .finally(() => {
        this.initModalData();
        setTimeout(() => {
          this.restoringCount--;
        }, 850);
      });
  }
}

function sameStopPath(
  shortestPathStopIds: number[] | null,
  coverageStopIds: number[] | null,
): boolean {
  return (
    !!shortestPathStopIds &&
    !!coverageStopIds &&
    shortestPathStopIds.length >= 2 &&
    shortestPathStopIds.length === coverageStopIds.length &&
    shortestPathStopIds.every(
      (stopId, index) => stopId === coverageStopIds[index],
    )
  );
}

function firstVisits(stopIds: number[] | null): number[] | null {
  return stopIds ? [...new Set(stopIds)] : null;
}
